import React, { useState, useEffect, useCallback } from 'react';
import { fetchExaminerNotes, createExaminerNote, updateExaminerNote } from '../api';
import { todayInGaborone } from '../utils/dates';

const STATUS_CONFIG = {
  OPEN:        { label: 'Open',        color: 'bg-red-100 text-red-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  RESOLVED:    { label: 'Resolved',    color: 'bg-green-100 text-green-700' },
  RE_LODGED:   { label: 'Re-lodged',   color: 'bg-blue-100 text-blue-700' },
};

const BLANK = {
  query_text: '', query_date: '', deadline_date: '',
  status: 'OPEN', resolution_notes: '', resolved_date: '', re_lodgement_date: '',
};

export default function ExaminerNotesList({ authAxios, showFormMessage, instrumentId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const today = todayInGaborone();

  const load = useCallback(async () => {
    if (!instrumentId) return;
    setLoading(true);
    try {
      const data = await fetchExaminerNotes(authAxios, instrumentId);
      setNotes(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage?.('error', 'Failed to load examiner notes.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, instrumentId, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, query_date: today, instrument: instrumentId });
    setShowForm(true);
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({ ...BLANK, ...n, instrument: instrumentId });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ['deadline_date', 'resolved_date', 're_lodgement_date'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });

      if (editing) {
        await updateExaminerNote(authAxios, editing.id, payload);
        showFormMessage?.('success', 'Note updated.');
      } else {
        await createExaminerNote(authAxios, payload);
        showFormMessage?.('success', 'Note created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Examiner Notes</h5>
        <button onClick={openCreate}
          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">
          + Add Query
        </button>
      </div>

      {loading && <p className="text-xs text-gray-400">Loading…</p>}

      <div className="space-y-2">
        {notes.map(n => {
          const sc = STATUS_CONFIG[n.status] ?? { label: n.status, color: 'bg-gray-100 text-gray-600' };
          const urgent = n.days_to_deadline !== null && n.days_to_deadline <= 2 && n.status !== 'RESOLVED';
          return (
            <div key={n.id} className={`border rounded-lg p-3 ${urgent ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-gray-800 flex-1">{n.query_text}</p>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${sc.color}`}>{sc.label}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-gray-500">
                <span>Queried: {n.query_date}</span>
                {n.deadline_date && (
                  <span className={n.days_to_deadline <= 2 ? 'text-red-600 font-medium' : ''}>
                    Deadline: {n.deadline_date} {n.days_to_deadline !== null ? `(${n.days_to_deadline}d)` : ''}
                  </span>
                )}
                {n.resolved_date && <span>Resolved: {n.resolved_date}</span>}
              </div>
              {n.resolution_notes && (
                <p className="mt-1 text-[11px] text-gray-600 italic">{n.resolution_notes}</p>
              )}
              <button onClick={() => openEdit(n)} className="mt-1 text-[11px] text-blue-600 hover:underline">Edit</button>
            </div>
          );
        })}
        {!loading && notes.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-2">No examiner queries recorded.</p>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-sm">{editing ? 'Edit Examiner Note' : 'New Examiner Query'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Query Text *</label>
                <textarea required value={form.query_text} onChange={e => fld('query_text', e.target.value)}
                  rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Query Date *</label>
                  <input required type="date" value={form.query_date} onChange={e => fld('query_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Deadline Date</label>
                  <input type="date" value={form.deadline_date} onChange={e => fld('deadline_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => fld('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(STATUS_CONFIG).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Resolution Notes</label>
                <textarea value={form.resolution_notes} onChange={e => fld('resolution_notes', e.target.value)}
                  rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Resolved Date</label>
                  <input type="date" value={form.resolved_date} onChange={e => fld('resolved_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Re-lodgement Date</label>
                  <input type="date" value={form.re_lodgement_date} onChange={e => fld('re_lodgement_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
